/**
 * useCurrencyRates.js — load configurable CAD reference exchange rates from the API.
 */

import { useState, useEffect } from 'react';

export const DEFAULT_CURRENCY_RATES = {
  cadToUsdRate: 0.74,
  cadToEurRate: 0.68,
  cadToGbpRate: 0.57,
  cadToAudRate: 1.10,
};

let cachedRates = null;
let fetchPromise = null;

function normalizeLoadedRates(data) {
  return {
    cadToUsdRate: Number(data.cadToUsdRate) || DEFAULT_CURRENCY_RATES.cadToUsdRate,
    cadToEurRate: Number(data.cadToEurRate) || DEFAULT_CURRENCY_RATES.cadToEurRate,
    cadToGbpRate: Number(data.cadToGbpRate) || DEFAULT_CURRENCY_RATES.cadToGbpRate,
    cadToAudRate: Number(data.cadToAudRate) || DEFAULT_CURRENCY_RATES.cadToAudRate,
  };
}

export function invalidateCurrencyRatesCache() {
  cachedRates = null;
  fetchPromise = null;
}

export async function fetchCurrencyRates() {
  if (cachedRates) return cachedRates;
  if (fetchPromise) return fetchPromise;

  fetchPromise = fetch('/api/currency-rates')
    .then((response) => (response.ok ? response.json() : DEFAULT_CURRENCY_RATES))
    .then((data) => {
      cachedRates = normalizeLoadedRates(data);
      return cachedRates;
    })
    .catch(() => DEFAULT_CURRENCY_RATES)
    .finally(() => {
      fetchPromise = null;
    });

  return fetchPromise;
}

export function useCurrencyRates() {
  const [rates, setRates] = useState(cachedRates || DEFAULT_CURRENCY_RATES);

  useEffect(() => {
    let cancelled = false;
    fetchCurrencyRates().then((loaded) => {
      if (!cancelled) setRates(loaded);
    });
    return () => { cancelled = true; };
  }, []);

  return rates;
}
